import React, {
	useState,
	useMemo,
	useRef,
	useEffect,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import classNames from 'classnames';
import { ICONS } from '../../../general-components/icon';
import MainVideoEditing from '../../../main-video/main-video-editing';
import { GreenroomState as GS } from '../../../../stores/types/types';
import Socket, { ConnectedUser } from '../../../../connection/socket';
import Media from '../../../media/media';

import { VideoStream } from '../../../types/videos';
import { toHashmap } from '../../../../utils/utils';
import VideoFeedShotBuilder from './video-feed-shot-builder';

import { Invitation } from '../../../../stores/reducers/invitations';
import VideoFeedFakeThumbnail from '../../../right-panel/video-feed-fake-thumbnail';
import OverlaysSettings from '../../../right-panel/overlay-settings';

import RestrictedUserThumbnail from '../../../right-panel/restricted-user-thumbnail';
import RightDrawerOption from '../../../right-panel/right-drawer-option';
import { TitlesProvider } from '../../../../contexts/titles/titles-context';
import { META_MENU_ROUTES, META_MENU_STATES, SHOT_MODAL_TABS, SUB_TABS, TABS } from '../../../../stores/types/tabs';
import { ThumbnailLocation } from '../../../../types/enums';
import OnsiteLocationThumbnail from '../../../main-video/onsite-location/onsite-location-thumbnail';
import SrtStreamThumbnail from '../../../main-video/srt-stream/srt-stream-thumbnail';
import { OnsiteLocation, SrtStream, SrtStreamStatus } from '../../../../stores/reducers/video';
import { selectSrtStreams } from '../../../../stores/selectors/video';
import StylePackTabContainer from '../../../style-packs/style-packs-tab-container';
import useClickAwayListener from '../../../../hooks/use-click-away-listener';
import usePrevious from '../../../../utils/use-previous';
import { ShotModalStylepackDrawer } from './shot-builder';
import Heading from '../../../general-components/v2/heading';
import GenerateContentTitle from '../../../right-panel/generate-content-title';
import MediaSettings from '../../../right-panel/media-settings';
import { setSelectedMediaTemplate } from '../../../../stores/actions/generate-content';
import { setCurrentGraphicsId } from '../../../../stores/actions/graphics';
import { editOverlayDraft } from '../../../../connection/graphics';
import { UpsertMediaDraft } from '../../../../connection/media';
import { CSSTransition } from 'react-transition-group';
import { FkTable } from '../../../../stores/types/content';

interface Props {
	socket: Socket | null;
	modalPage?: string | undefined;
	modalTab?: any;
	shotModalStylepackDrawer: ShotModalStylepackDrawer;
	setShotModalStylepackDrawer: (stylepackDrawer: ShotModalStylepackDrawer) => void;
}

export default function ShotEditUI(props: Props) {
	const {
		socket,
		modalTab = TABS.FEEDS,
		shotModalStylepackDrawer,
		setShotModalStylepackDrawer,
	} = props;

	const token = useSelector((s: GS) => s.SessionReducer.token);
	const connectedUsers = useSelector((s: GS) => s.SessionReducer.connectedUsers);
	const connectedPresenterVideos = useSelector((s: GS) => s.VideoReducer.connectedPresenterVideos);
	const { standaloneSrtStreams, onsiteSrtStreams } = useSelector(selectSrtStreams);
	const onsiteLocations = useSelector((s: GS) => s.VideoReducer.onsiteLocations);
	const onsiteLocationStreams = useSelector((s: GS) => s.VideoReducer.onsiteLocationStreams);
	const shotModalShot = useSelector((s: GS) => s.ShotsReducer.shotModalShot);
	const invitations = useSelector((s: GS) => s.InvitationsReducer.invitations);
	const currGraphicId = useSelector((s: GS) => s.GraphicsReducer.currentGraphicId);
	const currMediaId = useSelector((s: GS) => s.GeneratedContentReducer.selectedMediaItem?.id ?? 0);
	const shotModalGraphics = useSelector((s: GS) => s.GraphicsReducer.shotModalGraphics);
	const selectedMediaTemplate = useSelector((s: GS) => s.GeneratedContentReducer.selectedMediaTemplate);
	const shotModalMedia = useSelector((s: GS) => s.MediaReducer.shotModalMedia);
	const generatedContent = useSelector((s: GS) => s.GeneratedContentReducer.generatedContent);
	const {
		use_srt_streams: useSrtStreams,
	} = useSelector((state: GS) => state.SettingsReducer.featureFlags);

	const dispatch = useDispatch();

	const [currentSideTab, setCurrentSideTab] = useState<TABS>(modalTab);
	const [previousSideTab, setPreviousSideTab] = useState(currentSideTab);

	const feedsRef = useRef<HTMLDivElement>(null);
	const stylePackContainerRef = useRef<HTMLDivElement>(null);
	const overlaySettingsRef = useRef<HTMLDivElement>(null);
	const mediaSettingsRef = useRef<HTMLDivElement>(null);

	const setSideTab = (tab: TABS | null) => {
		if (currentSideTab && !SUB_TABS.includes(currentSideTab)) {
			setPreviousSideTab(currentSideTab);
		}
		setCurrentSideTab(tab ?? previousSideTab ?? TABS.FEEDS);
	};

	const restrictedUsers = useMemo(() => connectedUsers.filter(
		(u: ConnectedUser) => u.in_room
			&& u.restrictedUserState
			&& u.restrictedUserState > 2 // cam only and mic only restricted users show as presenter feeds
			&& !u.watcher_only,
	), [connectedUsers]);

	// filter out invitations with watcher_only because watchers/viewers cannot be added to a shot
	const invitedUsersNotInRoom = useMemo(() => invitations.filter(
		(invitation: Invitation) => !connectedUsers.some(
			(conUser: any) => conUser.gr_user === invitation.profile.gr_user && conUser?.in_room,
		) && !invitation.watcher_only,
	), [connectedUsers, invitations]);

	const isNewShot = useMemo(
		(): boolean => shotModalShot.shot_id === 0,
		[shotModalShot],
	);

	const selectedGraphic = useMemo(
		() => shotModalGraphics.find((g) => g?.id === currGraphicId),
		[currGraphicId, shotModalGraphics],
	);

	const selectedGraphicName = useMemo(
		() => selectedGraphic?.title ?? 'Design',
		[selectedGraphic, shotModalGraphics],
	);

	const selectedMediaItem = useMemo(
		() => shotModalMedia.find((m) => m?.id === currMediaId),
		[currMediaId, shotModalMedia],
	);

	const selectedMediaName = useMemo(
		() => selectedMediaItem?.custom_name ?? selectedMediaItem?.original_name ?? 'Design',
		[selectedMediaItem],
	);

	const getGeneratedContentFromGraphic = useMemo(
		() => generatedContent?.find((c) => c.fkTable === FkTable.graphics && c.fkId === selectedGraphic?.id),
		[selectedGraphic, generatedContent],
	);

	const getGeneratedContentFromMediaItem = useMemo(
		() => generatedContent?.find((c) => c.fkTable === FkTable.media && c.fkId === selectedMediaItem?.id),
		[selectedMediaItem, generatedContent],
	);

	useClickAwayListener({
		ref: stylePackContainerRef,
		onClickAway: () => null,
		onMousedownClickAway: () => setShotModalStylepackDrawer({
			...shotModalStylepackDrawer,
			state: META_MENU_STATES.CLOSING,
		}),
		open: shotModalStylepackDrawer.state === META_MENU_STATES.OPEN,
		exemptClassnames: ['shot-builder-modal-style-pack-button']
	});

	useClickAwayListener({
		ref: overlaySettingsRef,
		onMousedownClickAway: () => {
			dispatch(setCurrentGraphicsId(0));
		},
		open: !!selectedGraphic,
		exemptClassnames: [
			'green-box',
			'moveable-control-box',
			'graphic-drag-area',
			'graphic-wrapper',
			'custom-graphics-child',
			'graphic-drag-area',
			'graphic-wrapper',
			'graphic-menu',
		],
	});

	useClickAwayListener({
		ref: mediaSettingsRef,
		onMousedownClickAway: (e) => {
			if (
				e.target instanceof HTMLVideoElement
				&& e.target.classList.contains('media-video')
				&& e.target.src === selectedMediaItem?.images
			) {
				return;
			}
			dispatch(setSelectedMediaTemplate(null, ''));
		},
		open: !!selectedMediaItem,
		exemptClassnames: [
			'video-hover-options',
			'video-hover-options-buttons',
			'video-hover-options-button',
			'graphic-menu',
		],
	});

	const prevSelGraphicId = usePrevious(currGraphicId);
	const prevSelectedMediaTemplate = usePrevious(selectedMediaTemplate);
	useEffect(() => {
		if (
			shotModalStylepackDrawer.state === META_MENU_STATES.OPEN && 
			(prevSelGraphicId !== currGraphicId || prevSelectedMediaTemplate !== selectedMediaTemplate)
		) {
			// close the stylepack drawer if a stylepack becomes selected
			setShotModalStylepackDrawer({
				...shotModalStylepackDrawer,
				state: META_MENU_STATES.CLOSING,
			});
		}
	}, [currGraphicId, prevSelGraphicId, selectedMediaTemplate, prevSelectedMediaTemplate]);

	return (<>
		<div className="info-modal-header align-items-center justify-content-space-between shot-builder-modal-header">
			<h4 className="bl-font-header margin-none padding-left-large">
				{isNewShot ? 'Add Shot Content' : 'Edit Shot Content'}
			</h4>
		</div>
		<div className="flex shot-builder-modal-body position-relative">
			<div id="shot-builder-modal-main-video-container" className="shot-builder-modal-main-video-container">
				<TitlesProvider
					titlesOn={shotModalShot.shot_data.videoTitlesOn}
					shotId={shotModalShot.shot_id}
					isShotModal={true}
				>
					<MainVideoEditing
						socket={socket}
						shotBeingEdited={shotModalShot}
						isForShotEditorModal={true}
						iterateLoadedMediaCount={() => { }}
					/>
				</TitlesProvider>
			</div>

			<div
				id="sbm-right-drawer"
				className={classNames('shot-builder-modal-right-drawer', {
					'shot-builder-modal-right-drawer-no-scroll': currentSideTab === SHOT_MODAL_TABS.MEDIA || !!selectedGraphic,
				})}
			>
				<div
					className="restricted-feeds-tooltip"
					id="modal-restricted-tooltip-background"
					ref={feedsRef}
				/>
				{currentSideTab === SHOT_MODAL_TABS.FEEDS
					&& <div className="video-thumbs tab-row padding-medium">
						{connectedPresenterVideos.map(
							(vid: VideoStream, i: number) => (
								<VideoFeedShotBuilder
									video={vid}
									key={i}
									usersMap={toHashmap('webrtcID', connectedUsers)}
									feedsRef={feedsRef}
								/>
							),
						)}

						{invitedUsersNotInRoom.map(
							(invite: Invitation, i: number) => (
								<VideoFeedFakeThumbnail
									invite={invite}
									key={i}
									isForShotModal={true}
								/>
							),
						)}

						{restrictedUsers.map(
							(rUser: ConnectedUser, i: number) => <RestrictedUserThumbnail
								key={i}
								restrictedUser={rUser}
								feedsRef={feedsRef}
								isForShotModal={true}
							/>,
						)}

						{useSrtStreams && standaloneSrtStreams.map((srtStream: SrtStream, i: number) => (
							<SrtStreamThumbnail
								key={i}
								srtStream={srtStream}
								location={ThumbnailLocation.ShotModal}
							/>
						))}

						{onsiteLocations && onsiteLocations.map(
							(onsiteLocation: OnsiteLocation, i: number) => {
								const locationStreams = onsiteLocationStreams.get(onsiteLocation.id);
								if (locationStreams?.length) {
									return locationStreams.map((locationStream, j) => (
										<OnsiteLocationThumbnail
											key={`${i}-${j}`}
											onsiteLocation={onsiteLocation}
											location={ThumbnailLocation.ShotModal}
											locationStream={locationStream}
										/>
									));
								}
								const srtStream = onsiteSrtStreams.find(
									(s) => s.status === SrtStreamStatus.active && s.id === onsiteLocation.srtId,
								);
								if (srtStream) {
									return (
										<OnsiteLocationThumbnail
											key={i}
											onsiteLocation={onsiteLocation}
											location={ThumbnailLocation.ShotModal}
											srtStream={srtStream}
										/>
									);
								}
								return (
									<OnsiteLocationThumbnail
										key={i}
										onsiteLocation={onsiteLocation}
										location={ThumbnailLocation.ShotModal}
									/>
								);
							},
						)}
					</div>
				}

				{currentSideTab === SHOT_MODAL_TABS.MEDIA && (
					<div className="shot-builder-modal-media-container height-full">
						<Media
							socket={socket}
							onScreenSharePrompt={() => { }}
							openScreenShareTab={false}
							isForShotModal={true} />
					</div>
				)}

				<CSSTransition
					in={!!selectedGraphic}
					timeout={300}
					classNames="slide"
					unmountOnExit
					nodeRef={overlaySettingsRef}
				>
					<div className='shot-builder-content-settings' ref={overlaySettingsRef}>
						<Heading
							title={
								<GenerateContentTitle
									generatedContent={getGeneratedContentFromGraphic}
									graphic={selectedGraphic}
									title={selectedGraphicName}
									saveCallback={(newName: string) => {
										if (selectedGraphic) {
											editOverlayDraft(
												token ?? '',
												{
													...selectedGraphic,
													title: newName,
												},
											);
										}
									}}
								/>
							}
							xButtonFunction={() => dispatch(setCurrentGraphicsId(0))}
						/>
						<OverlaysSettings
							forceHide={!selectedGraphic}
							isForShotEditorModal={true}
						/>
					</div>
				</CSSTransition>

				<CSSTransition
					in={!!selectedMediaItem}
					timeout={300}
					classNames="slide"
					unmountOnExit
					nodeRef={mediaSettingsRef}
				>
					<div className='shot-builder-content-settings' ref={mediaSettingsRef}>
						<Heading
							title={
								<GenerateContentTitle
									generatedContent={getGeneratedContentFromMediaItem}
									mediaItem={selectedMediaItem ?? undefined}
									title={selectedMediaName}
									saveCallback={(newName: string) => {
										if (selectedMediaItem) {
											UpsertMediaDraft(
												shotModalShot.meeting,
												{
													...selectedMediaItem,
													custom_name: newName,
												},
											);
										}
									}}
								/>
							}
							xButtonFunction={() => dispatch(setSelectedMediaTemplate(null, ''))}
						/>
						<MediaSettings
							isForShotEditorModal={true}
						/>
					</div>
				</CSSTransition>
			</div>

			<div className="shot-builder-modal-right-button-menu">
				<div className="right-drawer-menu">
					<RightDrawerOption
						name="Feeds"
						icon={ICONS.PRESENTERS}
						isActive={currentSideTab === SHOT_MODAL_TABS.FEEDS}
						onClick={() => setSideTab(SHOT_MODAL_TABS.FEEDS)}
					/>
					<RightDrawerOption
						name="Media"
						icon={ICONS.TAB_MEDIA}
						isActive={currentSideTab === SHOT_MODAL_TABS.MEDIA}
						onClick={() => setSideTab(SHOT_MODAL_TABS.MEDIA)}
					/>
				</div>
			</div>

			<div
				ref={stylePackContainerRef}
				className={classNames('shot-builder-modal-style-pack-container', shotModalStylepackDrawer.state)}
				onAnimationEnd={() => {
					const stateMap: Record<string, string> = {
						[META_MENU_STATES.OPENING]: META_MENU_STATES.OPEN,
						[META_MENU_STATES.CLOSING]: META_MENU_STATES.CLOSED,
					};
					const nextState = stateMap[shotModalStylepackDrawer.state];
					if (nextState) {
						setShotModalStylepackDrawer({
							...shotModalStylepackDrawer,
							state: nextState,
						});
					}
				}}
			>
				<StylePackTabContainer
					shouldRenderInfo={shotModalStylepackDrawer.showInfo}
					drawerState={shotModalStylepackDrawer.state}
					shotModalStylepackDrawer={shotModalStylepackDrawer}
					setShotModalStylepackDrawer={setShotModalStylepackDrawer}
				/>
			</div>
		</div>

		</>
	);
}
